import chunk from "lodash-es/chunk";
import isEqual from "lodash-es/isEqual";
import { reaction } from "mobx";
import { apiClient } from "../../common/apiClient";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import { ORDERTYPE } from "../../common/types/productOrderTypes";
import type { ProductAvailability } from "../../common/types/productTypes";
import { formatM3Date } from "../../common/utils/dateUtils";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import deliveryDatesStore from "../deliveryDates/deliveryDatesStore";
import posthogStore from "../posthogStore";
import cartStore from "./cartStore";

// DSP-2628: silent pilot comparing availability seen at cart time with availability at order submission.
// Remove this module and its three call sites when the pilot ends.
export const CART_AVAILABILITY_FLAG = "cart-availability-observation-v1";
const EVENT = "Cart Availability Checked";
const DEBOUNCE_MS = 1000;
const MAX_PRODUCTS_PER_REQUEST = 10;

let ignoringAvailabilityChanges = false;

type Phase = "cart" | "submit";
type Context = {
   store_id: string;
   customer_number: string;
   requested_delivery_date: string;
   order_type: string;
};

const getContext = (): Context | null => {
   if (!posthogStore.posthog?.isFeatureEnabled(CART_AVAILABILITY_FLAG)) return null;
   const customerNumber = authStore.currentCompany;
   const delivery = deliveryDatesStore.getCurrentDelivery();
   if (
      theme.storeId !== "tinehandel" ||
      !theme.productAvailability.enabled ||
      !customerNumber ||
      !delivery ||
      cartStore.isEditing() ||
      cartStore.orderType === ORDERTYPE.WAS
   ) {
      return null;
   }
   return {
      store_id: theme.storeId,
      customer_number: customerNumber,
      requested_delivery_date: formatM3Date(delivery.date),
      order_type: cartStore.orderType
   };
};

const capture = (phase: Phase, context: Context, quantities: Map<string, number>, availabilities: ProductAvailability[]) => {
   for (const [sku, qty] of quantities) {
      const availability = availabilities.find((item) => item.productNumber === sku);
      if (!availability) continue;
      posthogStore.captureEvent(EVENT, {
         ...context,
         phase,
         product_number: sku,
         requested_quantity: qty,
         available: availability.available,
         available_quantity: availability.availableQuantity,
         delayed_delivery_date: availability.delayedDeliveryDate
      });
   }
};

const requestAvailability = (context: Context, quantities: Map<string, number>): Promise<ProductAvailability[]> =>
   apiClient(
      resolvePlatformRequestUrl(platformRoutes.productAvailability, { storeId: context.store_id }),
      authStore.getSessionToken()
   )
      .options({ cache: "no-store" })
      .query({
         customerNumber: context.customer_number,
         m3OrderType: context.order_type,
         requestedDeliveryDate: context.requested_delivery_date,
         products: [...quantities].map(([sku, qty]) => `${sku}*${qty}`).join("|")
      })
      .get()
      .json<ProductAvailability[]>();

/** Checks the quantities the customer chose, as they were when the debounce ended. */
const checkCartQuantities = async (customerQuantities: Map<string, number>) => {
   // The changes belong to one customer, date, order type and cart. Stop when any of them changes,
   // or when the customer submits, because a result after that is no longer a cart-time observation.
   const context = getContext();
   const items = cartStore.items;
   const isCurrent = () => !cartStore.sendingOrder && cartStore.items === items && isEqual(getContext(), context);
   // Drop lines the customer changed again or removed, or that the availability dialog adjusted
   const unchanged = (lines: [string, number][]) =>
      new Map(lines.filter(([sku, qty]) => cartStore.getCartItem(sku)?.qty === qty));

   if (!context) return;
   for (const batch of chunk([...customerQuantities], MAX_PRODUCTS_PER_REQUEST)) {
      if (!isCurrent()) return;
      const quantities = unchanged(batch);
      if (!quantities.size) continue;
      try {
         const availabilities = await requestAvailability(context, quantities);
         if (!isCurrent()) return;
         capture("cart", context, unchanged([...quantities]), availabilities);
      } catch (error) {
         logger.log("tracking", "cart availability check failed", error);
      }
   }
};

type SubmitAvailabilityTracker = (quantities: Record<string, number>, availabilities: ProductAvailability[]) => void;

/**
 * Call before the order submission requests availability. Returns a function that records the response
 * with the context the request was made for. Must never affect the order.
 */
export const startSubmitAvailabilityTracking = (): SubmitAvailabilityTracker => {
   let context: Context | null = null;
   try {
      context = getContext();
   } catch (error) {
      logger.log("tracking", "submit availability tracking failed", error);
   }
   return (quantities, availabilities) => {
      try {
         if (context) capture("submit", context, new Map(Object.entries(quantities)), availabilities);
      } catch (error) {
         logger.log("tracking", "submit availability tracking failed", error);
      }
   };
};

/** Called before the availability dialog changes the cart. Its quantities are not customer changes, so they are not checked. */
export const ignoreAvailabilityDialogChanges = () => {
   ignoringAvailabilityChanges = true;
   // The dialog applies its changes synchronously, and MobX runs the reaction before this microtask
   queueMicrotask(() => {
      ignoringAvailabilityChanges = false;
   });
};

/** Checks availability in the background after the customer changes a cart quantity. Returns a disposer. */
export const startCartAvailabilityExperiment = () => {
   const pending = new Set<string>();
   let timer: ReturnType<typeof setTimeout> | undefined;

   const stopReaction = reaction(
      () => ({ items: cartStore.items, quantities: new Map(cartStore.items.map((item) => [item.sku, item.qty])) }),
      (next, previous) => {
         // Restoring, clearing or logging out replaces the array. Customer quantity changes edit it in place.
         // Drop changes still waiting, so the replaced cart's quantities are not checked.
         if (next.items !== previous.items) {
            pending.clear();
            clearTimeout(timer);
            return;
         }
         for (const [sku, qty] of next.quantities) {
            if (qty <= 0 || qty === previous.quantities.get(sku)) continue;
            // Also drop a pending customer change, so it isn't checked at the dialog's quantity
            if (ignoringAvailabilityChanges) pending.delete(sku);
            else pending.add(sku);
         }
         if (!pending.size) return;
         clearTimeout(timer);
         timer = setTimeout(() => {
            const quantities = new Map(
               [...pending].map((sku) => [sku, cartStore.getCartItem(sku)?.qty ?? 0] as const).filter(([, qty]) => qty > 0)
            );
            pending.clear();
            void checkCartQuantities(quantities);
         }, DEBOUNCE_MS);
      }
   );

   return () => {
      stopReaction();
      clearTimeout(timer);
   };
};
